import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { selectData } from '@/api/envelope';
import { useGetPostEngagementChart } from '@/api/generated/post-manager/post-manager';
import { EngagementChart, type EngagementSeriesDatum } from '@/components/charts/EngagementChart';
import {
  RangePresetPicker,
  useRangePreset,
  CONTENT_PRESETS,
  type RangeDays,
} from '@/components/charts/RangePresetPicker';
import { ENGAGEMENT_SERIES_COLORS, FALLBACK_SERIES_COLOR } from '@/lib/chartTheme';
import type { ChartRequestDTOUnit, EngagementResponseChartDTO } from '@/api/generated/model';

// Per-post engagement chart (doc 08 §3). Uses the real `getPostEngagementChart` endpoint
// (EngagementResponseChartDTO: purchase/like/comment/tip counts over time). The backend returns a
// full zero-filled series across [start,end] bucketed by `unit`, so an all-zero result means the
// post genuinely had no activity in the window — not an error. The range picker lets an admin widen
// the window (up to 1Y) to surface older activity.
const SERIES_DEFS: { name: string; pick: (d: EngagementResponseChartDTO) => number }[] = [
  { name: 'Purchases', pick: (d) => d.purchaseCount ?? 0 },
  { name: 'Likes', pick: (d) => d.likeCount ?? 0 },
  { name: 'Comments', pick: (d) => d.commentCount ?? 0 },
  { name: 'Tips', pick: (d) => d.tipCount ?? 0 },
];

// Coarser buckets for wider windows so the x-axis stays readable (backend buckets by ChronoUnit).
function bucketUnit(days: RangeDays): ChartRequestDTOUnit {
  if (days <= 30) return 'Days';
  if (days <= 90) return 'Weeks';
  return 'Months';
}

export function ContentEngagementPane({ postId }: { postId: number }) {
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
  const { days, setDays, startISO, endISO } = useRangePreset(30);

  // toISOString() (Z-suffixed) is required by the admin-api LocalDateTimeConverter (Instant.parse).
  const query = useGetPostEngagementChart(
    postId,
    { requestDTO: { startDateTime: startISO, endDateTime: endISO, unit: bucketUnit(days) } },
    { query: { enabled: postId > 0, select: selectData } },
  );

  const { categories, series, hasActivity } = useMemo(() => {
    const rows = query.data ?? [];
    const cats = rows.map((r) => r.localDateTime ?? '');
    const s: EngagementSeriesDatum[] = SERIES_DEFS.map((def) => {
      const data = rows.map(def.pick);
      return {
        name: def.name,
        color: ENGAGEMENT_SERIES_COLORS[def.name] ?? FALLBACK_SERIES_COLOR,
        data,
        total: data.reduce((a, b) => a + b, 0),
      };
    });
    return { categories: cats, series: s, hasActivity: s.some((x) => x.total > 0) };
  }, [query.data]);

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-sm">Engagement</CardTitle>
        <RangePresetPicker days={days} onChange={setDays} presets={CONTENT_PRESETS} />
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="size-5 animate-spin text-primary" />
          </div>
        ) : query.isError ? (
          <div className="flex h-64 items-center justify-center text-sm text-destructive">
            Failed to load engagement data.
          </div>
        ) : categories.length === 0 ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            No engagement data for this period.
          </div>
        ) : (
          <>
            <EngagementChart
              categories={categories}
              series={series}
              hiddenSeries={hiddenSeries}
              onLegendChange={setHiddenSeries}
            />
            {!hasActivity && (
              <p className="mt-1 text-center text-xs text-muted-foreground">
                No engagement recorded in this window — try a wider range.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
